/*
 * Tester-unit opt-in for the wholesale cart.
 *
 * On a bulk cart line (no `Tester` line item property) whose product has the
 * `custom.tester_available` metafield set and whose own quantity is >= 6, the
 * Liquid in sections/main-cart-items.liquid renders a <tester-line-toggle> with a
 * checkbox next to that line.
 *
 *  - Checking it adds a SECOND cart line for the same variant: quantity 1, line
 *    item property { Tester: "Yes — 50% cost applied" }.
 *  - Unchecking it removes that specific tester line.
 *  - If the parent bulk line later drops below 6 units or is removed, the now
 *    orphaned tester line is cleaned up automatically on the next cart update.
 *
 * The `Tester` property key and a non-empty value are the contract the
 * mmsco-wholesale-api backend reads to detect tester lines — do not rename the
 * key or prefix it with an underscore (underscore keys are hidden from the
 * customer-facing cart, order, and packing slip).
 *
 * Cart mutations reuse the theme's conventions: fetchConfig(), the AJAX cart
 * routes, section re-render from the `sections` payload, and PUB_SUB_EVENTS.
 * This does not touch the MinCart Order Limits app embed.
 */
if (!customElements.get('tester-line-toggle')) {
  const TESTER_PROPERTY_KEY = 'Tester';
  const TESTER_PROPERTY_VALUE = 'Yes — 50% cost applied';
  const MIN_BULK_QUANTITY = 6;

  const isTesterLine = (item) =>
    Boolean(item.properties && item.properties[TESTER_PROPERTY_KEY]);

  const cartItemsElement = () => document.querySelector('cart-items');

  const sectionsToRender = () => {
    const el = cartItemsElement();
    return el && typeof el.getSectionsToRender === 'function' ? el.getSectionsToRender() : [];
  };

  const renderCartSections = (state) => {
    if (!state || !state.sections) return;
    sectionsToRender().forEach((section) => {
      const container = document.getElementById(section.id);
      const markup = state.sections[section.section];
      if (!container || markup == null) return;
      const target = container.querySelector(section.selector) || container;
      const fresh = new DOMParser()
        .parseFromString(markup, 'text/html')
        .querySelector(section.selector);
      if (fresh) target.innerHTML = fresh.innerHTML;
    });
  };

  const cartFetch = async (url, payload) => {
    const body = JSON.stringify({
      ...payload,
      sections: sectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    const response = await fetch(url, { ...fetchConfig(), body });
    const state = await response.json();
    if (state.status || state.errors || state.message) {
      throw new Error(state.description || state.message || 'Cart request failed');
    }
    return state;
  };

  const getCart = () => fetch(`${routes.cart_url}.js`).then((response) => response.json());

  // Tester lines with no matching bulk line (same variant, no Tester property,
  // quantity >= 6) are orphans and should be removed.
  const orphanedTesterKeys = (items) =>
    items
      .filter(isTesterLine)
      .filter(
        (tester) =>
          !items.some(
            (line) =>
              !isTesterLine(line) &&
              line.id === tester.id &&
              line.quantity >= MIN_BULK_QUANTITY
          )
      )
      .map((tester) => tester.key);

  let cleanupRunning = false;

  const cleanupOrphanTesterLines = async (cartData) => {
    if (cleanupRunning) return;
    const cart = cartData && Array.isArray(cartData.items) ? cartData : await getCart();
    const keys = orphanedTesterKeys(cart.items);
    if (keys.length === 0) return;

    cleanupRunning = true;
    try {
      let state;
      for (const key of keys) {
        state = await cartFetch(routes.cart_change_url, { id: key, quantity: 0 });
      }
      renderCartSections(state);
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'tester-line', cartData: state });
    } catch (error) {
      console.error('[tester-line] failed to remove orphaned tester line', error);
    } finally {
      cleanupRunning = false;
    }
  };

  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event.source === 'tester-line') return;
    cleanupOrphanTesterLines(event.cartData);
  });

  class TesterLineToggle extends HTMLElement {
    constructor() {
      super();
      this.checkbox = this.querySelector('input[type="checkbox"]');
      this.variantId = Number(this.dataset.variantId);
      if (this.checkbox) {
        this.checkbox.addEventListener('change', this.onToggle.bind(this));
      }
    }

    setBusy(busy) {
      this.toggleAttribute('aria-busy', busy);
      if (this.checkbox) this.checkbox.disabled = busy;
    }

    async onToggle() {
      const shouldAdd = this.checkbox.checked;
      this.setBusy(true);
      try {
        const state = shouldAdd ? await this.addTesterLine() : await this.removeTesterLine();
        if (state) {
          renderCartSections(state);
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'tester-line', cartData: state });
        }
      } catch (error) {
        console.error('[tester-line] toggle failed', error);
        this.checkbox.checked = !shouldAdd; // revert the visual state
      } finally {
        this.setBusy(false);
      }
    }

    async addTesterLine() {
      const cart = await getCart();
      const alreadyThere = cart.items.some(
        (item) => isTesterLine(item) && item.id === this.variantId
      );
      if (alreadyThere) return null;
      return cartFetch(routes.cart_add_url, {
        items: [
          {
            id: this.variantId,
            quantity: 1,
            properties: { [TESTER_PROPERTY_KEY]: TESTER_PROPERTY_VALUE },
          },
        ],
      });
    }

    async removeTesterLine() {
      const cart = await getCart();
      const line = cart.items.find(
        (item) => isTesterLine(item) && item.id === this.variantId
      );
      if (!line) return null;
      return cartFetch(routes.cart_change_url, { id: line.key, quantity: 0 });
    }
  }

  customElements.define('tester-line-toggle', TesterLineToggle);
}
